import {
  SdsSection3,
  SdsSection3Material,
  SdsSection3ImpurityMaterial,
  SdsSection3Cas,
  SdsSection3ImpurityCas,
} from '@/interfaces/model/general/sds';

// API interfaces for the unified backend structure
interface ApiCasItem {
  id: number;
  sds_id: string;
  sds_section3_material_id: number;
  cas_number: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface ApiMaterialItem {
  id: number;
  sds_id: string;
  sds_section3_id: number;
  material_name: string;
  concentration: string;
  encs_number: string;
  ishl_number: string;
  concentration_inequality_sign: number;
  concentration_min: number | null;
  concentration_max: number | null;
  concentration_unit: string;
  is_stated: boolean;
  is_impurity: boolean;
  // Parent relationship fields (used by both materials and impurities)
  parent_material_id?: number | null;
  temp_material_id?: string;
  temp_parent_material_id?: string;
  cas_list: ApiCasItem[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface ApiSection3 {
  id: number;
  sds_id: string;
  sds_product_id: number;
  composition_id: number;
  synonyms: string[];
  aliases: string[];
  chemical_properties: string[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  material_list: ApiMaterialItem[];
}

/**
 * Convert unified API response (with is_impurity flag) to separate frontend structure
 */
export const convertApiResponseToFrontend = (
  apiSection3: ApiSection3
): SdsSection3 => {
  // Split materials by is_impurity flag
  const materials: SdsSection3Material[] = [];
  const impurities: SdsSection3ImpurityMaterial[] = [];

  if (apiSection3.material_list) {
    apiSection3.material_list.forEach((item: ApiMaterialItem) => {
      if (item.is_impurity === true) {
        // Convert to impurity structure
        const impurity: SdsSection3ImpurityMaterial = {
          id: item.id,
          sds_id: item.sds_id,
          sds_section3_id: item.sds_section3_id,
          material_name: item.material_name,
          concentration: item.concentration,
          encs_number: item.encs_number,
          ishl_number: item.ishl_number,
          concentration_inequality_sign: item.concentration_inequality_sign,
          concentration_min: item.concentration_min,
          concentration_max: item.concentration_max,
          concentration_unit: item.concentration_unit,
          is_stated: item.is_stated,
          // Parent relationship fields (mapped from unified API fields)
          parent_impurity_material_id: item.parent_material_id,
          temp_impurity_material_id: item.temp_material_id,
          temp_parent_impurity_material_id: item.temp_parent_material_id,
          cas_list: item.cas_list.map(
            (cas: ApiCasItem): SdsSection3ImpurityCas => ({
              id: cas.id,
              sds_id: cas.sds_id,
              sds_section3_impurity_material_id: cas.sds_section3_material_id, // Field name conversion
              cas_number: cas.cas_number,
              created_at: cas.created_at,
              updated_at: cas.updated_at,
              deleted_at: cas.deleted_at,
            })
          ),
          created_at: item.created_at,
          updated_at: item.updated_at,
          deleted_at: item.deleted_at,
        };
        impurities.push(impurity);
      } else {
        // Keep as regular material
        const material: SdsSection3Material = {
          id: item.id,
          sds_id: item.sds_id,
          sds_section3_id: item.sds_section3_id,
          material_name: item.material_name,
          concentration: item.concentration,
          encs_number: item.encs_number,
          ishl_number: item.ishl_number,
          concentration_inequality_sign: item.concentration_inequality_sign,
          concentration_min: item.concentration_min,
          concentration_max: item.concentration_max,
          concentration_unit: item.concentration_unit,
          is_stated: item.is_stated,
          is_impurity: item.is_impurity,
          // Parent material relationship fields
          parent_material_id: item.parent_material_id,
          temp_material_id: item.temp_material_id,
          temp_parent_material_id: item.temp_parent_material_id,
          cas_list: item.cas_list.map(
            (cas: ApiCasItem): SdsSection3Cas => ({
              id: cas.id,
              sds_id: cas.sds_id,
              sds_section3_material_id: cas.sds_section3_material_id,
              cas_number: cas.cas_number,
              created_at: cas.created_at,
              updated_at: cas.updated_at,
              deleted_at: cas.deleted_at,
            })
          ),
          created_at: item.created_at,
          updated_at: item.updated_at,
          deleted_at: item.deleted_at,
        };
        materials.push(material);
      }
    });
  }

  // Return frontend structure with separate lists
  return {
    id: apiSection3.id,
    sds_id: apiSection3.sds_id,
    sds_product_id: apiSection3.sds_product_id,
    composition_id: apiSection3.composition_id,
    synonyms: apiSection3.synonyms,
    aliases: apiSection3.aliases,
    chemical_properties: apiSection3.chemical_properties,
    created_at: apiSection3.created_at,
    updated_at: apiSection3.updated_at,
    deleted_at: apiSection3.deleted_at,
    material_list: materials,
    impurity_list: impurities,
  };
};

/**
 * Convert separate frontend structure to unified API request (with is_impurity flag)
 */
export const convertFrontendToApiRequest = (
  frontendSection3: SdsSection3
): ApiSection3 => {
  const unifiedMaterials: ApiMaterialItem[] = [];

  // Add regular materials with is_impurity: false
  if (frontendSection3.material_list) {
    frontendSection3.material_list.forEach((material) => {
      const apiMaterial = {
        id: material.id,
        sds_id: material.sds_id,
        sds_section3_id: material.sds_section3_id,
        material_name: material.material_name,
        concentration: material.concentration,
        encs_number: material.encs_number,
        ishl_number: material.ishl_number,
        concentration_inequality_sign: material.concentration_inequality_sign,
        concentration_min: material.concentration_min,
        concentration_max: material.concentration_max,
        concentration_unit: material.concentration_unit,
        is_stated: material.is_stated,
        is_impurity: false, // Flag for regular materials
        // Parent material relationship fields
        parent_material_id: material.parent_material_id,
        temp_material_id: material.temp_material_id,
        temp_parent_material_id: material.temp_parent_material_id,
        cas_list: material.cas_list.map((cas) => ({
          id: cas.id,
          sds_id: cas.sds_id,
          sds_section3_material_id: cas.sds_section3_material_id,
          cas_number: cas.cas_number,
          created_at: cas.created_at,
          updated_at: cas.updated_at,
          deleted_at: cas.deleted_at,
        })),
        created_at: material.created_at,
        updated_at: material.updated_at,
        deleted_at: material.deleted_at,
      };
      unifiedMaterials.push(apiMaterial);
    });
  }

  // Add impurity materials with is_impurity: true
  if (frontendSection3.impurity_list) {
    frontendSection3.impurity_list.forEach((impurity) => {
      const apiMaterial = {
        id: impurity.id,
        sds_id: impurity.sds_id,
        sds_section3_id: impurity.sds_section3_id,
        material_name: impurity.material_name,
        concentration: impurity.concentration,
        encs_number: impurity.encs_number,
        ishl_number: impurity.ishl_number,
        concentration_inequality_sign: impurity.concentration_inequality_sign,
        concentration_min: impurity.concentration_min,
        concentration_max: impurity.concentration_max,
        concentration_unit: impurity.concentration_unit,
        is_stated: impurity.is_stated,
        is_impurity: true, // Flag for impurity materials
        // Parent relationship fields (mapped to unified API fields)
        parent_material_id: impurity.parent_impurity_material_id,
        temp_material_id: impurity.temp_impurity_material_id,
        temp_parent_material_id: impurity.temp_parent_impurity_material_id,
        cas_list: impurity.cas_list.map((cas) => ({
          id: cas.id,
          sds_id: cas.sds_id,
          sds_section3_material_id: cas.sds_section3_impurity_material_id, // Field name conversion back
          cas_number: cas.cas_number,
          created_at: cas.created_at,
          updated_at: cas.updated_at,
          deleted_at: cas.deleted_at,
        })),
        created_at: impurity.created_at,
        updated_at: impurity.updated_at,
        deleted_at: impurity.deleted_at,
      };
      unifiedMaterials.push(apiMaterial);
    });
  }

  // Return API structure with unified material_list
  return {
    id: frontendSection3.id,
    sds_id: frontendSection3.sds_id,
    sds_product_id: frontendSection3.sds_product_id,
    composition_id: frontendSection3.composition_id,
    synonyms: frontendSection3.synonyms,
    aliases: frontendSection3.aliases,
    chemical_properties: frontendSection3.chemical_properties,
    created_at: frontendSection3.created_at,
    updated_at: frontendSection3.updated_at,
    deleted_at: frontendSection3.deleted_at,
    material_list: unifiedMaterials,
  };
};
